/**
 * Pure formatting and grouping helpers for citations.
 *
 * Kept out of the component files so each of those exports components only,
 * which is what React Fast Refresh needs to hot-reload them reliably.
 */
import type { Citation } from "../../types/agent-chat";

/**
 * Short label for a chip.
 *
 * Document citations show their ordinal alone, because in a French HR answer
 * "3" reads as a footnote and needs no prefix. Web and CNP citations keep a
 * marker of their kind, because that is the useful signal: a reader wants to
 * know at a glance that a figure came from outside the corpus.
 */
export function chipLabel(id: string): string {
  if (id.startsWith("S")) return id.slice(1);
  if (id.startsWith("C")) return "CNP";
  return id;
}

/** One line describing where inside the source the passage sits. */
export function locatorSummary(citation: Citation): string {
  const l = citation.locator;
  if (l.type === "char_range") {
    // `exact` is false when the chunk text could not be found in the parent
    // document and the position fell back to the chunk index.
    return l.exact
      ? `Document ${l.document_id}, caractères ${l.start} à ${l.end}`
      : `Document ${l.document_id}, passage ${(l.chunk_index ?? 0) + 1} (position approximative)`;
  }
  if (l.type === "url") {
    try {
      return new URL(l.url).hostname;
    } catch {
      return l.url;
    }
  }
  const pages =
    l.page_start != null
      ? l.page_end != null && l.page_end !== l.page_start
        ? `pages ${l.page_start} à ${l.page_end}`
        : `page ${l.page_start}`
      : null;
  return [`Code ${l.code}`, pages].filter(Boolean).join(", ");
}

/** The URL of a web citation, or null for any other kind. */
export function citationUrl(citation: Citation): string | null {
  return citation.locator.type === "url" ? citation.locator.url : null;
}

export interface SourceGroup {
  dedupeKey: string;
  title: string;
  kind: Citation["kind"];
  /** Every citation id that resolved to this source, in id order. */
  ids: string[];
  /** Best score across the group; null when none carried one. */
  bestScore: number | null;
  representative: Citation;
}

/**
 * Collapse citations to one row per underlying source.
 *
 * Several chunks of one document, or several passages of one web page, are one
 * source to a reader even though the model cites them separately.
 */
export function groupCitations(citations: Citation[]): SourceGroup[] {
  const byKey = new Map<string, SourceGroup>();
  for (const c of citations) {
    const existing = byKey.get(c.dedupe_key);
    if (existing) {
      existing.ids.push(c.id);
      if (c.score != null && (existing.bestScore == null || c.score > existing.bestScore)) {
        existing.bestScore = c.score;
      }
    } else {
      byKey.set(c.dedupe_key, {
        dedupeKey: c.dedupe_key,
        title: c.title,
        kind: c.kind,
        ids: [c.id],
        bestScore: c.score,
        representative: c,
      });
    }
  }
  return [...byKey.values()].map((g) => ({ ...g, ids: [...g.ids].sort() }));
}
